import { useCallback, useContext, useEffect, useRef } from 'react';
import { ActiveEditorContext } from '../ActiveEditorContext';
import type { Editor, EditorEvents } from '@tiptap/react';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Transaction } from '@tiptap/pm/state';
import { isHistoryTransaction } from '@tiptap/pm/history';
import { useSectionData } from '../../../Sections/useSectionContext.ts';
import { SelectionContext } from '../../SelectedBlockInfoProvider.tsx';
import type { TableType } from '../../../grid/reduxStore/table.types.ts';
import { setOfRowsForPricingTable, setOfRowsForPricingTableWithReferences } from '../../Block/Table/defaultData.tsx';
import { getRowAttributeName } from '../../../../../muiTheme/dataGridUtils.ts';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../grid/reduxStore/Store.ts';
import { useAppSelector } from '../../../grid/reduxStore/Store.ts';
import { selectContentTable } from '../../../grid/reduxStore/editorSlice.ts';
import { useTableContentUndoRedo } from '../../../hooks/useTableContentUndoRedo.ts';
import { useTiptapUndoRedoCommands } from '../hooks/useUndoRedoCommands';
import { getTableBlockExtensionsForEditor } from '../helpers/config.ts';
import useFeatureFlag from '../../../../../hooks/useFeatureFlag.ts';
import { FeatureFlags } from '../../../../../utils/featureFlags.ts';
import { selectAllVariablesItems } from '../../../grid/reduxStore/allVariableSlice.ts';
import { isJson } from '../../../../../utils/isJson.ts';
import { selectContentType } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { ContentType } from 'utils/getContentType';

export const TipTapPricingFooterTitle = ({ id, hasFocus }) => {
  const { setActiveEditor } = useContext(ActiveEditorContext);
  const { sectionId } = useSectionData();
  const { handleTableContentChanged } = useTableContentUndoRedo();
  const { selectedBlockIdByWrapper } = useContext(SelectionContext);
  const isCurrencySettingsEnabled = useFeatureFlag([FeatureFlags.currencySettings])[FeatureFlags.currencySettings];
  const allVariables = useAppSelector(selectAllVariablesItems);
  const disableSuggestions = useAppSelector(selectContentType) === ContentType.SECTION;

  const blockId = selectedBlockIdByWrapper as string;
  const tableData = useSelector((state: RootState) => selectContentTable(state, sectionId, blockId)) || undefined;
  const rowsForPricingTable = isCurrencySettingsEnabled ? setOfRowsForPricingTableWithReferences : setOfRowsForPricingTable;
  const totalTableRows = tableData?.totalRows || rowsForPricingTable;
  const currentRow = totalTableRows?.find((row) => row.id === id);
  const rawCellValue = currentRow?.['title'];
  const cellValue = rawCellValue && isJson(rawCellValue) ? JSON.parse(rawCellValue) : rawCellValue;
  const handleInputChange = async (value: string) => {
    const updatedRows = totalTableRows?.map((row) => (row.id === id ? { ...row, ['title']: value } : row));

    const newTableData: TableType = {
      ...tableData,
      totalRows: updatedRows,
      metadata: {
        ...tableData?.metadata,
      },
    };

    handleTableContentChanged(blockId, sectionId, tableData, newTableData);
  };

  // Select-all + typing replaces the whole doc, which recreates the paragraph
  // node from scratch and drops data-table-style. Restore it from the row's
  // type so the footer title keeps its total/subtotal styling.
  //
  // setCustomParagraph dispatches its own transaction, which re-enters
  // onUpdate synchronously before this call returns. isRestoringRowStyleRef
  // marks that re-entrant call so it skips redoing the persist logic below —
  // the outer call (this one) already re-reads the editor's corrected state
  // once the fix has been applied and persists exactly once.
  const isRestoringRowStyleRef = useRef(false);
  const restoreRowStyleIfMissing = (editor: Editor, transaction: Transaction) => {
    if (isHistoryTransaction(transaction)) return;

    const cellParagraph = editor.state.doc.firstChild;
    if (currentRow && cellParagraph?.type.name === 'paragraph' && !cellParagraph.attrs['data-table-style']) {
      isRestoringRowStyleRef.current = true;
      editor.commands.setCustomParagraph({ rowType: getRowAttributeName(currentRow) });
      isRestoringRowStyleRef.current = false;
    }
  };

  const updateEditorData = useCallback(
    async (editor: Editor, transaction: Transaction) => {
      if (isRestoringRowStyleRef.current) return;

      restoreRowStyleIfMissing(editor, transaction);
      const json = JSON.stringify(editor.getJSON());
      await handleInputChange(json);
    },
    [handleInputChange]
  );

  const editor = useEditor({
    content: cellValue ?? '',
    extensions: getTableBlockExtensionsForEditor({
      variables: allVariables ?? [],
      disableSuggestions,
    }),
    onUpdate: ({ editor, transaction }: EditorEvents['update']) => updateEditorData(editor, transaction),
    editable: hasFocus,
  });

  useTiptapUndoRedoCommands(editor);

  useEffect(() => {
    if (hasFocus && editor) {
      setActiveEditor(editor);
    }
  }, [editor, setActiveEditor, hasFocus]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(hasFocus, false);
      if (hasFocus) {
        editor.commands.focus();
      }
    }
  }, [hasFocus, editor]);

  useEffect(() => {
    if (editor) {
      requestAnimationFrame(() => {
        if (!editor.isDestroyed) {
          editor.commands.focus('end');
        }
      });
    }
  }, [editor]);

  return editor ? <EditorContent editor={editor} /> : null;
};
